// components/GlobalChatSheet.tsx — V28 C3: global swipe-up "Chiedi a Il Prof" sheet.
//
// The single slide-up overlay that lets a student ask Il Prof a question without
// leaving the page they're on. React port of the prototype chat-sheet (markup
// `docs/specs/v28-prototype/index.html` 3344-3367, CSS 1365-1386).
//
// Mounted ONCE by AppFrame so every chat-enabled student route shares one sheet
// instance. It reads `useChatSheet()` for open/close (V29: the ChatDock pill is
// the opener — the V28 swipe-up gesture + SwipeHint were retired with the shell
// cutover); a swipe-down ≥60px, the drag-handle tap, or a scrim tap all close().
//
// The body is <ProfChatPanel> (C3). On NON-lesson routes it renders the SAME
// shared Il Prof conversation that `/prof` renders, backed by the persisted
// Zustand store (thread id 'chiedi', `chiediSessionId`). The panel is always
// mounted — keeping it mounted lets the slide transition play on close AND
// preserves the conversation as the sheet opens/closes across screens (the
// store survives, but keeping the subtree alive avoids a flash of empty state
// on each open).
//
// Founder chat-scoping contract (Item B, 2026-06-11; supersedes R2-13):
//
// The in-lesson / in-sub-argomento Prof chat is a DEDICATED, per-VISIT, topic-
// scoped conversation — never the main /prof 'chiedi' history. The sheet only
// MOUNTS on chat-enabled surfaces (screen-flow `chat: true`); hub pages
// (Progressi cockpit + drill-down, Appendice index, Simula) are `chat: false`,
// so the sheet never appears there.
//   - On a SCOPED surface (a specific lesson OR a specific Appendice
//     sub-argomento) the sheet uses a per-visit thread id
//     `<surface>:<topicId>:<visitNonce>` minted by `useScopedChat`. The SAME
//     visit keeps the thread (and one BE session — "it keeps going"); LEAVING and
//     RETURNING mints a new nonce → a fresh thread/session. The scoped thread
//     persists its OWN BE session (mode='chiedi') so it shows in Cronologia.
//   - On the main /prof the global 'chiedi' thread is used EXACTLY as before.
//
// Light-only (build-contract): all colors come from `useTokens()` (tokens.mode
// always resolves 'light'); no token key is dropped. Motion is suppressed under
// `useReducedMotion()` (OS preference OR the in-app a11y toggle).

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChatSheet } from '../state/chat-sheet';
import { useReducedMotion } from '../state/use-reduced-motion';
import { useScreenFlow } from '../state/screen-flow';
import { useSwipeGestures } from '../hooks/useSwipeGestures';
import { useTokens } from '../state/theme';
import { useScopedChat, scopedTopicKeyFor } from '../state/scoped-chat';
import { useChat } from '../state/chat';
import { useActiveLessonContext } from '../state/active-lesson-context';
import { useActiveProfFigure } from '../state/active-prof-figure';
import { selectProfFigure } from '../state/prof-figure';
import { ProfAvatar } from './ProfAvatar';
import { ProfChatPanel } from './ProfChatPanel';

// Prototype easing for the slide (index.html line 1371). Held as a constant so
// the open + closed styles share it and reduced-motion can drop it wholesale.
const SHEET_EASE = 'cubic-bezier(.22,.61,.36,1)';

export function GlobalChatSheet(): JSX.Element {
  const { isOpen, close, open } = useChatSheet();
  const reducedMotion = useReducedMotion();
  const tokens = useTokens();
  const navigate = useNavigate();

  // Item B (founder chat-scoping contract) — pick the chat thread by CONTEXT.
  //
  // The chat sheet is only ever MOUNTED on a chat-enabled surface (screen-flow's
  // `chat: true`). Hub pages (Progressi cockpit + drill-down, Appendice index,
  // Simula) have `chat: false`, so the sheet never appears there. On the surfaces
  // where it DOES appear:
  //   - SCOPED surfaces (a specific lesson OR a specific sub-argomento in
  //     Appendice): a per-VISIT thread `<surface>:<topicId>:<visitNonce>`. The
  //     nonce is minted by `useScopedChat` on every fresh ENTER of that topic, so
  //     the SAME visit keeps one thread (and one BE session — "it keeps going")
  //     and a RETURN mints a fresh one. Per-thread BE session persistence means
  //     the conversation is stored in Cronologia on leave.
  //   - the main /prof (the only non-scoped chat-enabled surface): the global
  //     'chiedi' thread, exactly as before.
  //
  // The topic id is derived from the PATHNAME (not useParams() — GlobalChatSheet
  // is mounted in AppFrame ABOVE <Routes>, so useParams() returns {} here).
  const { key: flowKey } = useScreenFlow();
  const { pathname } = useLocation();
  const enterScopedTopic = useScopedChat((s) => s.enterScopedTopic);
  const leaveScopedTopic = useScopedChat((s) => s.leaveScopedTopic);
  const clearScopedThread = useChat((s) => s.clearScopedThread);

  const topicKey = scopedTopicKeyFor(flowKey, pathname);

  // The resolved thread id for the panel. For a scoped surface we mint/keep the
  // per-visit thread in an effect (a store mutation can't run during render);
  // until it resolves we hold the topicKey so the panel still reads a SEPARATE
  // thread (never 'chiedi').
  const [panelThreadId, setPanelThreadId] = useState<string>('chiedi');

  useEffect(() => {
    if (topicKey) {
      // Entering (or staying within) a scoped topic — mint/keep the visit thread.
      setPanelThreadId(enterScopedTopic(topicKey));
    } else {
      // A non-scoped chat-enabled surface (the main /prof) — leave any scoped
      // topic so the NEXT scoped enter is fresh, and use the global thread.
      leaveScopedTopic();
      setPanelThreadId('chiedi');
    }
    // topicKey fully captures the surface+topic identity; re-run on change.
  }, [topicKey, enterScopedTopic, leaveScopedTopic]);

  // Item B — local thread hygiene: when the active scoped thread CHANGES (the
  // student switched lesson/sub-argomento or left for a hub page), drop the
  // PREVIOUS scoped thread's local messages + session id so localStorage / memory
  // don't accumulate one thread per visited lesson/sub-argomento. The
  // conversation already lives in the BE / Cronologia. A ref tracks the prior id
  // so the cleanup compares the OLD value against the NEW one correctly.
  const prevThreadIdRef = useRef<string>('chiedi');
  useEffect(() => {
    const prev = prevThreadIdRef.current;
    if (prev !== panelThreadId) {
      if (prev !== 'chiedi') clearScopedThread(prev);
      prevThreadIdRef.current = panelThreadId;
      // Founder contract: leaving a scoped surface ARCHIVES the chat — an open
      // sheet must not ride along to the next page (it visibly survived
      // navigation to chat-disabled hubs in the live re-test). Closing on any
      // scope change is safe: on first resolve the sheet is still closed, and
      // the conversation itself is already persisted server-side.
      close();
    }
  }, [panelThreadId, clearScopedThread, close]);

  const isLessonThread = panelThreadId !== 'chiedi';

  // Item 2 (founder round-3, 2026-06-11) — on a LESSON thread, forward the
  // active lesson context (published by GuidedLessonScreen) so the in-lesson
  // Prof answers as the FULL Prof with a lesson bias (never refuses a
  // driving/exam question, anchors "questo argomento" to the lesson). Only
  // surfaced when the panel thread is a `lesson:*` thread whose argomento
  // matches the published context — never on /prof or an Appendice thread.
  const activeLessonContext = useActiveLessonContext((s) => s.context);
  const lessonContextForPanel =
    isLessonThread &&
    panelThreadId.startsWith('lesson:') &&
    activeLessonContext
      ? activeLessonContext
      : undefined;

  const figureSelection = useActiveProfFigure((s) => s.selection);
  useEffect(() => {
    if (!figureSelection) return;
    if (useActiveProfFigure.getState().selection?.selectionId !== figureSelection.selectionId) return;
    if (figureSelection.topicKey !== topicKey || figureSelection.pathname !== pathname) {
      useActiveProfFigure.getState().clearSelection(figureSelection.selectionId);
      return;
    }
    // Wait for the real per-visit nonce; never attach to the previous panel.
    if (!topicKey || !panelThreadId.startsWith(`${topicKey}:`)) return;
    useActiveProfFigure.getState().clearSelection(figureSelection.selectionId);
    void selectProfFigure(panelThreadId, figureSelection);
    open();
  }, [figureSelection, topicKey, pathname, panelThreadId, open]);

  // Drag-to-close: a swipe-down ≥60px ON THE HANDLE dismisses the sheet.
  // Pilot phone-test (2026-07-07): this used to be attached to the whole
  // sheet, so scrolling the chat history downward closed it by accident —
  // same defect class as the frame-level swipe-up. The handle (which also
  // closes on tap) is the only drag surface now.
  const { ref: dragRef } = useSwipeGestures({ onSwipeDown: close });

  // The sheet appears on non-/prof screens that have no local voice engine. Its
  // composer mic DICTATES into the text field (it-IT Web-Speech, owned by the
  // Composer — no onMic prop). The call icon routes to /prof (which owns
  // useQwenRealtimeVoice) to start the voice-call there. This keeps the composer
  // identical everywhere (field + mic + call + send).
  const handleSheetCall = () => {
    close();
    navigate('/prof', { state: { startCall: true } });
  };

  return (
    <>
      {/* Scrim — tap to close. Kept mounted; fades in/out via opacity so the
          sheet's own slide transition isn't interrupted by an unmount. */}
      <div
        aria-hidden="true"
        onClick={close}
        style={{
          position:       'absolute',
          inset:          0,
          zIndex:         60,
          background:     'rgba(26,21,53,0.32)', // prototype scrim (line 1366)
          opacity:        isOpen ? 1 : 0,
          visibility:     isOpen ? 'visible' : 'hidden',
          pointerEvents:  isOpen ? 'auto' : 'none',
          transition:     reducedMotion ? 'none' : 'opacity .2s ease',
        }}
      />

      {/* The sheet itself. Always mounted; slides via translateY so closing
          animates. While closed it's pulled fully off-screen, made
          non-interactive and hidden from the a11y tree. */}
      <div
        role="dialog"
        aria-label="Chiedi a Il Prof"
        aria-hidden={!isOpen}
        style={{
          position:      'absolute',
          left:          0,
          right:         0,
          bottom:        0,
          zIndex:        61,
          height:        '86%',
          background:    tokens.bg,
          borderRadius:  '22px 22px 0 0',
          boxShadow:     '0 -16px 40px rgba(26,21,53,0.22)', // prototype (line 1370)
          transform:     isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition:    reducedMotion ? 'none' : `transform .26s ${SHEET_EASE}`,
          display:       'flex',
          flexDirection: 'column',
          overflow:      'hidden',
          // Don't let a closed (off-screen) sheet trap pointer or focus.
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        {/* Drag handle — tap (or drag-down on it) closes. The dragRef lives
            HERE, not on the sheet, so scrolling the thread never closes it. */}
        <button
          type="button"
          ref={dragRef}
          onClick={close}
          aria-label="Chiudi la chat"
          style={{
            touchAction:    'none',
            flex:           '0 0 auto',
            display:        'flex',
            flexDirection:  'column',
            alignItems:     'center',
            gap:            8,
            padding:        '8px 0 6px',
            border:         'none',
            background:     'transparent',
            cursor:         'pointer',
            font:           'inherit',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width:        42,
              height:       5,
              borderRadius: tokens.radius.pill,
              background:   tokens.textFaint, // muted lavender grip
            }}
          />
          <span
            style={{
              fontSize:      11,
              fontWeight:    700,
              color:         tokens.textMuted,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Scorri giù per chiudere
          </span>
        </button>

        {/* Sheet header — Prof avatar + title/subtitle. */}
        <div
          style={{
            flex:        '0 0 auto',
            display:     'flex',
            alignItems:  'center',
            gap:         10,
            padding:     '2px 16px 10px',
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <ProfAvatar size={30} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: tokens.text }}>
              Chiedi a Il Prof
            </div>
            <div style={{ fontSize: 11.5, color: tokens.textMuted }}>
              {isLessonThread
                ? 'su questa lezione, qui'
                : 'qui, senza lasciare la pagina'}
            </div>
          </div>
        </div>

        {/* Body — on non-lesson routes the SAME shared Il Prof conversation as
            /prof; on a lesson route a CONFINED, lesson-scoped thread that never
            touches the global 'chiedi' history or its BE session (R2-13).
            `compact` trims the panel chrome to sit flush under the sheet head.
            The mic DICTATES (no onMic prop — the Composer owns it-IT Web-Speech).
            onCall is forwarded so the sheet's call icon routes to /prof (the
            voice-engine host); the composer still shows mic + call + send
            identical to /prof. */}
        <ProfChatPanel
          compact
          placeholder={isLessonThread ? 'Chiedi su questa lezione…' : 'Scrivi a Il Prof…'}
          threadId={panelThreadId}
          {...(lessonContextForPanel ? { lessonContext: lessonContextForPanel } : {})}
          // Item B — BOTH the global 'chiedi' thread AND a scoped thread persist
          // a BE session (the panel branches internally: global → chiediSessionId,
          // scoped → its own per-thread scopedSessionIds entry). The scoped
          // session is what makes the visit "keep going" and appear in Cronologia.
          persistSession
          onCall={handleSheetCall}
        />
      </div>
    </>
  );
}
